import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import {
  NgbDate,
  NgbCalendar,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateAdapter,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import {
  I18n,
  DatepickerI18nService,
  CustomAdapter,
  CustomDateParserFormatter,
  DateRangeModel,
} from 'src/app/components/datepicker-range/datepicker-i18n.service';
import * as moment from 'moment';

@Component({
  selector: 'app-datepicker-range',
  templateUrl: './datepicker-range.component.html',
  styleUrls: ['./datepicker-range.component.scss'],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: DatepickerI18nService },
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class DatepickerRangeComponent {
  model: string;

  hoveredDate: NgbDate | null = null;

  fromDate: NgbDate | null;
  toDate: NgbDate | null;

  @Input() fromDateInput: Date;
  @Input() toDateInput: Date;

  @Output() dateRangeChange: EventEmitter<DateRangeModel> = new EventEmitter();

  constructor(private calendar: NgbCalendar, public formatter: NgbDateParserFormatter) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fromDateInput && changes.fromDateInput.currentValue) {
      if (this.fromDateInput instanceof Date) {
        this.fromDate = NgbDate.from({
          year: this.fromDateInput.getFullYear(),
          month: this.fromDateInput.getMonth() + 1,
          day: this.fromDateInput.getDate(),
        });
      }
    } else {
      this.fromDate = null;
    }

    if (changes.toDateInput && changes.toDateInput.currentValue) {
      if (this.fromDateInput instanceof Date) {
        this.toDate = NgbDate.from({
          year: this.toDateInput.getFullYear(),
          month: this.toDateInput.getMonth() + 1,
          day: this.toDateInput.getDate(),
        });
      }
    } else {
      this.toDate = null;
    }
  }

  onDateSelection(date: NgbDate, datepicker: NgbInputDatepicker) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate) {
      if (date.before(this.fromDate)) {
        this.toDate = this.fromDate;
        this.fromDate = date;
      } else {
        this.toDate = date;
      }
    } else {
      this.toDate = null;
      this.fromDate = date;
    }

    if (this.fromDate && this.toDate) {
      this.emitChanges();
      datepicker.close();
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : null;
  }

  emitChanges() {
    let fromDate;
    let toDate;

    if (this.fromDate && this.toDate) {
      fromDate = new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day);
      toDate = new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day);
    } else {
      fromDate = this.fromDate;
      toDate = this.toDate;
    }

    const dateRangeModel = { fromDate, toDate };
    this.dateRangeChange.emit(dateRangeModel);
  }

  setToday(datepicker: NgbInputDatepicker) {
    this.fromDate = this.calendar.getToday();
    this.toDate = this.calendar.getToday();

    this.emitChanges();
    datepicker.close();
  }

  setYesterday(datepicker: NgbInputDatepicker) {
    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 1);
    this.toDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 1);

    this.emitChanges();
    datepicker.close();
  }

  setLastSevenDays(datepicker: NgbInputDatepicker) {
    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 6);
    this.toDate = this.calendar.getToday();

    this.emitChanges();
    datepicker.close();
  }

  setLastThirtyDays(datepicker: NgbInputDatepicker) {
    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 29);
    this.toDate = this.calendar.getToday();

    this.emitChanges();
    datepicker.close();
  }

  setThisMonth(datepicker: NgbInputDatepicker) {
    const firstDay = moment().startOf('month');
    const lastDay = moment().endOf('month');
    const fromDay = moment().diff(firstDay, 'days');
    const toDay = lastDay.diff(moment(), 'days', true);

    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', fromDay);
    this.toDate = this.calendar.getNext(this.calendar.getToday(), 'd', toDay);

    this.emitChanges();
    datepicker.close();
  }

  setLastMonth(datepicker: NgbInputDatepicker) {
    const firstDay = moment().subtract(1, 'month').startOf('month');
    const lastDay = moment().subtract(1, 'month').endOf('month');
    const fromDay = moment().diff(firstDay, 'days', true);
    const toDay = moment().diff(lastDay, 'days', true);

    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', fromDay);
    this.toDate = this.calendar.getPrev(this.calendar.getToday(), 'd', toDay);

    this.emitChanges();
    datepicker.close();
  }

  setThisYear(datepicker: NgbInputDatepicker) {
    const firstDay = moment().startOf('year');
    const lastDay = moment().endOf('year');
    const fromDay = moment().diff(firstDay, 'days');
    const toDay = lastDay.diff(moment(), 'days', true);

    this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', fromDay);
    this.toDate = this.calendar.getNext(this.calendar.getToday(), 'd', toDay);

    this.emitChanges();
    datepicker.close();
  }
}
